// Text for the roll log, built so a reader can add the shown numbers up to each total.

/**
 * What a reaction or a delayed effect rolled, from `combat.eventLog`, as lines for the log. Null for every other event. The
 * server rolls these, so they come to the log through the combat state, not through anyone's dice.
 */
export function describeCombatEvent(event = {}) {
  const dice = (rolls) => (rolls?.length > 1 ? ` (rolled ${rolls.join(' and ')})` : '');
  if (event.type === 'counterspell') {
    return { label: 'Counterspell', lines: [`Counterspell: ${event.by} → ${event.spellName}`,
      `${event.casterName} Constitution save: ${event.total}${dice(event.rolls)} vs DC ${event.dc}`, event.success ? `Saved: ${event.spellName} goes ahead` : `Failed: ${event.spellName} has no effect`] };
  }
  if (event.type === 'hellishRebuke') {
    const { save = {} } = event;
    return { label: 'Hellish Rebuke', lines: [`Hellish Rebuke: ${event.by} → ${event.targetName}`,
      `Dexterity save: ${save.total}${dice(save.rolls)} vs DC ${save.dc}: ${save.success ? 'succeeded' : 'failed'}`, `Damage: ${event.rolled}${save.success ? ', halved' : ''} = ${event.damage} Fire`] };
  }
  if (event.type === 'delayedDamage') {
    return { label: event.spellName, lines: [`${event.spellName}: ${event.targetName}`, `Damage at the end of its turn: ${event.dice} = ${event.damage} ${event.damageType}`] };
  }
  if (event.type === 'turnRoll') return { label: event.spellName, lines: [event.spellName, event.line] };
  return null;
}

const signed = (value) => (value < 0 ? `− ${Math.abs(value)}` : `+ ${value}`);

/** "2d8 (6, 4)"; Great Weapon Fighting shows a raised die as "1→3"; extra critical dice are counted in. */
export function formatDiceGroups(dice = []) {
  const groups = new Map();
  dice.forEach((die) => {
    if (!Number.isFinite(Number(die?.sides)) || !Number.isFinite(Number(die?.value))) return;
    const shown = Number.isFinite(Number(die.effectiveValue)) && Number(die.effectiveValue) !== Number(die.value)
      ? `${die.value}→${die.effectiveValue}` : `${die.value}`;
    const key = Number(die.sides);
    groups.set(key, [...(groups.get(key) || []), shown]);
  });
  return [...groups].map(([sides, values]) => `${values.length}d${sides} (${values.join(', ')})`);
}

/** One damage source: "1d8 (5) + 3 STR = 8 slashing", "Rage + 2", "Sneak Attack: 2d6 (3, 5) = 8 piercing". */
export function describeDamagePart(part = {}) {
  const pieces = formatDiceGroups(part.dice);
  let text = pieces.join(' + ');
  const flat = Number(part.flat) || 0;
  const ability = Number(part.ability) || 0;
  if (flat) text = text ? `${text} ${signed(flat)}` : `${flat}`;
  if (ability) text = text ? `${text} ${signed(ability)} ${part.abilityLabel || 'modifier'}` : `${ability} ${part.abilityLabel || 'modifier'}`;
  const type = part.type ? ` ${part.type}` : '';
  const value = Number(part.value) || 0;
  // A lone flat bonus (Rage) reads as "+ 2"; anything with dice or several pieces shows its sum.
  const body = pieces.length || (flat && ability) ? `${text} = ${value}${type}` : `${signed(value)}${type}`;
  return part.label ? `${part.label}: ${body}` : body;
}

/** A rolled feature component (Sneak Attack, Divine Smite) as a damage part. */
export function componentToPart(component = {}) {
  const sides = Number(component.dice?.sides);
  const rolls = Array.isArray(component.rolls) ? component.rolls : [];
  const effective = Array.isArray(component.effectiveRolls) ? component.effectiveRolls : [];
  return {
    label: component.label || 'Bonus damage',
    type: component.resolvedDamageType || component.type || '',
    dice: rolls.map((value, index) => ({ sides, value, ...(Number.isFinite(effective[index]) && effective[index] !== value ? { effectiveValue: effective[index] } : {}) })),
    flat: Number(component.flat ?? component.flatTotal) || 0,
    ability: 0,
    value: Number(component.total) || 0,
  };
}

const traitName = (traits) => (traits.immunity ? 'immunity' : [traits.resistance && 'resistance', traits.vulnerability && 'vulnerability'].filter(Boolean).join(' + '));
const titleCase = (text = '') => String(text).replace(/^./, (letter) => letter.toUpperCase());

/**
 * Lines explaining a targeted attack from roll to HP: each damage source, the rolled total, then anything that
 * changed it (a defender's reaction, damage traits, the Resistance cantrip) and the damage taken.
 */
export function describeAttackResolution(entry = {}) {
  const lines = [];
  if (entry.sunderingBlowEffectId) lines.push('Sundering Blow: +5 to this attack roll');
  if (entry.peerlessAim) lines.push('Peerless Aim: the miss became a hit');
  if (entry.strokeOfLuck) lines.push('Stroke of Luck: the d20 became a 20 (Critical Hit)');
  if (entry.heroicInspiration) lines.push(`Heroic Inspiration: rerolled the d20 (${entry.heroicInspiration.from} → ${entry.heroicInspiration.to})`);
  if (entry.fate) lines.push(`Boon of Fate: +${entry.fate.bonus} to the attack roll (2d4: ${entry.fate.rolls.join(', ')})`);
  if (entry.bardicDie) lines.push(`Bardic Inspiration: +${entry.bardicDie.bonus} to the attack roll (d${entry.bardicDie.sides})`);
  if (entry.peerlessSkill) lines.push(`Peerless Skill: +${entry.peerlessSkill.bonus} to the attack roll (d${entry.peerlessSkill.sides})${entry.peerlessSkill.spent ? '' : ', use kept'}`);
  if (entry.cuttingWords) lines.push(`Cutting Words (${entry.cuttingWords.by}): −${entry.cuttingWords.penalty} to the ${entry.cuttingWords.kind === 'damage' ? 'damage' : 'attack roll'} (d${entry.cuttingWords.sides})`);
  if (entry.empoweredStrikes) lines.push('Empowered Strikes: Force damage');
  if (entry.fateAgainst) lines.push(`Improve Fate (${entry.fateAgainst.by}): −${entry.fateAgainst.penalty} to the attack roll (2d4: ${entry.fateAgainst.rolls.join(', ')})`);
  const rolled = Number(entry.damage) || 0;
  if (entry.grazeApplied) {
    lines.push(`Graze: ${entry.grazeDamage ?? entry.damageApplied}${entry.damageType ? ` ${entry.damageType}` : ''} damage`);
  } else if (entry.missDamage) {
    lines.push(`${entry.missDamage.label}: ${entry.missDamage.amount}${entry.damageType ? ` ${entry.damageType}` : ''} damage`);
  } else if (entry.outcome !== 'miss') {
    const parts = [...(Array.isArray(entry.damageParts) ? entry.damageParts : []), ...(Array.isArray(entry.damageComponents) ? entry.damageComponents.map(componentToPart) : [])];
    parts.forEach((part) => lines.push(describeDamagePart(part)));
    const partsTotal = parts.reduce((sum, part) => sum + (Number(part.value) || 0), 0);
    // Older callers that do not report their roll still get an honest single line.
    if (!parts.length || partsTotal !== rolled) lines.push(`Damage rolled: ${rolled}`);
    else if (parts.length > 1) lines.push(`Total rolled: ${rolled}`);
  }
  const beforeTraits = Number(entry.damageBeforeTraits);
  if (entry.outcome !== 'miss' && !entry.grazeApplied && !entry.missDamage && Number.isFinite(beforeTraits) && beforeTraits !== rolled) {
    const names = (entry.incomingDamageModifiers || entry.defenderReaction?.incomingDamageModifiers || [])
      .map((modifier) => modifier.label || (modifier.featureId === 'rogue:uncanny-dodge' ? 'Uncanny Dodge' : modifier.featureId)).filter(Boolean);
    lines.push(`${names.length ? names.join(', ') : 'Reaction'}: ${rolled} → ${beforeTraits}`);
  }
  // The Resistance cantrip reduces damage before damage traits apply.
  if (entry.resistanceReduction) {
    const reduction = entry.resistanceReduction;
    lines.push(`Resistance cantrip: d4 ${reduction.die}, − ${reduction.reduction} ${reduction.type}`);
  }
  if (entry.damageTraits) {
    const before = Number.isFinite(beforeTraits) ? beforeTraits : rolled;
    const trait = `${titleCase(entry.damageTraits.type || entry.damageType || 'Damage')} ${traitName(entry.damageTraits)}`;
    lines.push(entry.resistanceReduction ? `${trait} applies` : `${trait}: ${before} → ${entry.damageTraits.afterTraits ?? entry.damageApplied ?? 0}`);
  }
  if (entry.outcome !== 'miss' || entry.grazeApplied || entry.missDamage) {
    lines.push(`Damage taken: ${entry.damageApplied ?? 0}${Number.isFinite(Number(entry.hpBefore)) ? ` · HP ${entry.hpBefore} → ${entry.hpAfter}` : ''}`);
  }
  return lines;
}

/** Healing: "2d8 (5, 3) + 3 = 11", then what each recipient actually gained (healing stops at max HP). */
export function describeHealing({ dice = [], sides, modifier = 0, rolledTotal } = {}) {
  const values = Array.isArray(dice) ? dice.filter((value) => Number.isFinite(Number(value))) : [];
  if (!values.length) return null;
  const sum = values.reduce((total, value) => total + Number(value), 0);
  const bonus = Number.isFinite(Number(rolledTotal)) ? Number(rolledTotal) - sum : Number(modifier) || 0;
  return `${values.length}d${sides || '?'} (${values.join(', ')})${bonus ? ` ${signed(bonus)}` : ''} = ${sum + bonus}`;
}
